import { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import api from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  User, 
  Plus,
  Eye,
  CheckCircle,
  AlertCircle,
  Edit,
  Camera,
  Mail,
  Shirt,
  Ruler,
  AlertTriangle
} from 'lucide-react';

const API_BASE = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';

const ParentChildren = () => {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailError, setEmailError] = useState('');
  
  // Player Profile Dialog
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedPlayerDetails, setSelectedPlayerDetails] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [uploadingPlayerPicture, setUploadingPlayerPicture] = useState(false);
  const playerPictureInputRef = useRef(null);
  const [editProfileData, setEditProfileData] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    skill_level: '',
    medical_notes: '',
    player_email: '',
    player_mobile: '',
    shirt_name: '',
    shirt_number: '',
    chest_size: '',
    shirt_length: '',
    sleeves: '',
    waist_size: '',
    trouser_length: ''
  });
  
  // Add Player Dialog
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [playerFormData, setPlayerFormData] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    skill_level: 'beginner'
  });

  const fetchData = async () => {
    try {
      const playersRes = await api.get('/players');
      setPlayers(playersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const viewPlayerProfile = async (playerId) => {
    setLoadingProfile(true);
    setProfileDialogOpen(true);
    setIsEditingProfile(false);
    try {
      const response = await api.get(`/players/${playerId}/details`);
      setSelectedPlayerDetails(response.data);
      setEditProfileData({
        full_name: response.data.full_name || '',
        date_of_birth: response.data.date_of_birth || '',
        gender: response.data.gender || '',
        skill_level: response.data.skill_level || '',
        medical_notes: response.data.medical_notes || '',
        player_email: response.data.player_email || '',
        player_mobile: response.data.player_mobile || '',
        shirt_name: response.data.shirt_name || '',
        shirt_number: response.data.shirt_number || '',
        chest_size: response.data.chest_size || '',
        shirt_length: response.data.shirt_length || '',
        sleeves: response.data.sleeves || '',
        waist_size: response.data.waist_size || '',
        trouser_length: response.data.trouser_length || ''
      });
    } catch (error) {
      toast.error('Failed to load player profile');
      setProfileDialogOpen(false);
    } finally {
      setLoadingProfile(false);
    }
  };

  const startEditProfile = () => {
    setIsEditingProfile(true);
  };

  const cancelEditProfile = () => {
    if (selectedPlayerDetails) {
      setEditProfileData({
        full_name: selectedPlayerDetails.full_name || '',
        date_of_birth: selectedPlayerDetails.date_of_birth || '',
        gender: selectedPlayerDetails.gender || '',
        skill_level: selectedPlayerDetails.skill_level || '',
        medical_notes: selectedPlayerDetails.medical_notes || '',
        player_email: selectedPlayerDetails.player_email || '',
        player_mobile: selectedPlayerDetails.player_mobile || '',
        shirt_name: selectedPlayerDetails.shirt_name || '',
        shirt_number: selectedPlayerDetails.shirt_number || '',
        chest_size: selectedPlayerDetails.chest_size || '',
        shirt_length: selectedPlayerDetails.shirt_length || '',
        sleeves: selectedPlayerDetails.sleeves || '',
        waist_size: selectedPlayerDetails.waist_size || '',
        trouser_length: selectedPlayerDetails.trouser_length || ''
      });
    }
    setIsEditingProfile(false);
  };

  const saveProfileChanges = async () => {
    if (!selectedPlayerDetails) return;
    
    // Validate player email is not same as parent email
    if (editProfileData.player_email && user?.email) {
      if (editProfileData.player_email.toLowerCase().trim() === user.email.toLowerCase().trim()) {
        setEmailError('Player email cannot be the same as your (parent) email. The player email will be used for your child\'s own login account.');
        toast.error('Player email cannot be same as parent email');
        return;
      }
    }
    setEmailError('');
    
    try {
      const dataToSave = {
        ...editProfileData,
        shirt_number: editProfileData.shirt_number ? parseInt(editProfileData.shirt_number) : null,
        chest_size: editProfileData.chest_size ? parseInt(editProfileData.chest_size) : null,
        shirt_length: editProfileData.shirt_length ? parseFloat(editProfileData.shirt_length) : null,
        waist_size: editProfileData.waist_size ? parseInt(editProfileData.waist_size) : null,
        trouser_length: editProfileData.trouser_length ? parseFloat(editProfileData.trouser_length) : null,
        sleeves: editProfileData.sleeves || null,
        shirt_name: editProfileData.shirt_name || null
      };
      
      await api.put(`/players/${selectedPlayerDetails.id}`, dataToSave);
      toast.success('Profile updated successfully');
      
      const response = await api.get(`/players/${selectedPlayerDetails.id}/details`);
      setSelectedPlayerDetails(response.data);
      setIsEditingProfile(false);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Failed to update profile');
      }
    }
  };

  const handlePlayerPictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPlayerDetails) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPlayerPicture(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/players/${selectedPlayerDetails.id}/profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated');
      setSelectedPlayerDetails(prev => ({ ...prev, profile_picture: response.data.profile_picture }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingPlayerPicture(false);
      if (playerPictureInputRef.current) {
        playerPictureInputRef.current.value = '';
      }
    }
  };

  const getPlayerPictureUrl = (picturePath) => {
    if (!picturePath) return null;
    if (picturePath.startsWith('http')) return picturePath;
    return `${API_BASE}${picturePath}`;
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/players', playerFormData);
      toast.success('Child added successfully');
      setPlayerDialogOpen(false);
      setPlayerFormData({ full_name: '', date_of_birth: '', gender: '', skill_level: 'beginner' });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Failed to add child');
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Family">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Family">
      <div className="space-y-6" data-testid="children-page">
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="font-heading text-xl">My Family</CardTitle>
              <Dialog open={playerDialogOpen} onOpenChange={setPlayerDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-child-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Child
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Add New Child</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPlayer} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        value={playerFormData.full_name}
                        onChange={(e) => setPlayerFormData({ ...playerFormData, full_name: e.target.value })}
                        placeholder="Child's full name"
                        required
                        data-testid="child-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={playerFormData.date_of_birth}
                        onChange={(e) => setPlayerFormData({ ...playerFormData, date_of_birth: e.target.value })}
                        required
                        data-testid="child-dob-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Gender</Label>
                        <Select
                          value={playerFormData.gender}
                          onValueChange={(value) => setPlayerFormData({ ...playerFormData, gender: value })}
                        >
                          <SelectTrigger data-testid="child-gender-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Skill Level</Label>
                        <Select
                          value={playerFormData.skill_level}
                          onValueChange={(value) => setPlayerFormData({ ...playerFormData, skill_level: value })}
                        >
                          <SelectTrigger data-testid="child-skill-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setPlayerDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="save-child-btn">
                        Add Child
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {players.length > 0 && players.some(p => !p.player_email) && (
              <div className="p-4 bg-purple-50 border-b border-purple-200" data-testid="add-email-prompt">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">Enable Player Login for Your Children</p>
                    <p className="text-xs text-purple-600 mt-1">
                      {players.filter(p => !p.player_email).length} of your children don't have an email address yet. 
                      Add their email to let them log in and view their own schedule and enrollments.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {players.length === 0 ? (
              <div className="p-12 text-center">
                <User className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No children added yet. Add your first child!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {players.map((player) => (
                  <div 
                    key={player.id}
                    className="p-3 lg:p-4 flex items-center gap-2.5 lg:gap-3 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => viewPlayerProfile(player.id)}
                    data-testid={`child-${player.id}`}
                  >
                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-blue-600 font-bold text-sm lg:text-lg">
                        {player.full_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-medium text-sm lg:text-base text-blue-600 truncate">{player.full_name}</p>
                        {player.player_email ? (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] lg:text-xs rounded-full flex items-center gap-0.5 whitespace-nowrap shrink-0">
                            <CheckCircle className="h-2.5 w-2.5 lg:h-3 lg:w-3" />
                            Login
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] lg:text-xs rounded-full whitespace-nowrap shrink-0">
                            No Login
                          </span>
                        )}
                      </div>
                      <p className="text-xs lg:text-sm text-slate-500 capitalize">
                        {player.skill_level} • Age: {player.age || 'N/A'}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 h-7 text-xs lg:h-9 lg:text-sm"
                      onClick={(e) => { e.stopPropagation(); viewPlayerProfile(player.id); }}
                      data-testid={`view-profile-${player.id}`}
                    >
                      <Eye className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1" />
                      <span className="hidden sm:inline">View Profile</span>
                      <span className="sm:hidden">View</span>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Player Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={(open) => {
          setProfileDialogOpen(open);
          if (!open) setIsEditingProfile(false);
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-500" />
                  Player Profile
                </span>
                {selectedPlayerDetails && !loadingProfile && !isEditingProfile && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={startEditProfile}
                    data-testid="edit-profile-btn"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit Profile
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {loadingProfile ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div>
              </div>
            ) : selectedPlayerDetails && (
              <div className="space-y-6 mt-4">
                {/* View Mode */}
                {!isEditingProfile && (
                  <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="relative group">
                        <div className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden border-4 border-white shadow-lg bg-blue-500">
                          {getPlayerPictureUrl(selectedPlayerDetails.profile_picture) ? (
                            <img 
                              src={getPlayerPictureUrl(selectedPlayerDetails.profile_picture)} 
                              alt={selectedPlayerDetails.full_name} 
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-bold text-2xl">
                              {selectedPlayerDetails.full_name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        
                        <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <input
                            type="file"
                            ref={playerPictureInputRef}
                            onChange={handlePlayerPictureUpload}
                            accept="image/*"
                            className="hidden"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-white hover:bg-white/20 h-8 w-8 p-0"
                            onClick={() => playerPictureInputRef.current?.click()}
                            disabled={uploadingPlayerPicture}
                          >
                            {uploadingPlayerPicture ? (
                              <span className="animate-spin text-sm">⏳</span>
                            ) : (
                              <Camera className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-bold text-slate-900">
                          {selectedPlayerDetails.full_name}
                        </h3>
                        <p className="text-slate-600">
                          Age: {selectedPlayerDetails.age || 'N/A'} • 
                          <span className="capitalize ml-1">{selectedPlayerDetails.skill_level || 'N/A'}</span> •
                          <span className="capitalize ml-1">{selectedPlayerDetails.gender || 'N/A'}</span>
                        </p>
                        {selectedPlayerDetails.date_of_birth && (
                          <p className="text-sm text-slate-500">
                            DOB: {new Date(selectedPlayerDetails.date_of_birth + 'T00:00:00').toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {selectedPlayerDetails.medical_notes && (
                      <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <AlertCircle className="h-4 w-4 inline mr-1" />
                          Medical Notes: {selectedPlayerDetails.medical_notes}
                        </p>
                      </div>
                    )}

                    {/* Uniform Information */}
                    {(selectedPlayerDetails.shirt_name || selectedPlayerDetails.shirt_number) && (
                      <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                        <p className="text-sm font-medium text-blue-800 flex items-center gap-2 mb-2">
                          <Shirt className="h-4 w-4" />
                          Uniform Information
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                          {selectedPlayerDetails.shirt_name && (
                            <p>Shirt Name: <span className="font-medium">{selectedPlayerDetails.shirt_name}</span></p>
                          )}
                          {selectedPlayerDetails.shirt_number && (
                            <p>Jersey #: <span className="font-medium">{selectedPlayerDetails.shirt_number}</span></p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Measurements */}
                    {(selectedPlayerDetails.chest_size || selectedPlayerDetails.waist_size || selectedPlayerDetails.shirt_length || selectedPlayerDetails.trouser_length || selectedPlayerDetails.sleeves) && (
                      <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                        <p className="text-sm font-medium text-green-800 flex items-center gap-2 mb-2">
                          <Ruler className="h-4 w-4" />
                          Measurements
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-green-700">
                          {selectedPlayerDetails.chest_size && (
                            <p>Chest: <span className="font-medium">{selectedPlayerDetails.chest_size}"</span></p>
                          )}
                          {selectedPlayerDetails.shirt_length && (
                            <p>Shirt Length: <span className="font-medium">{selectedPlayerDetails.shirt_length}"</span></p>
                          )}
                          {selectedPlayerDetails.sleeves && (
                            <p>Sleeves: <span className="font-medium capitalize">{selectedPlayerDetails.sleeves}</span></p>
                          )}
                          {selectedPlayerDetails.waist_size && (
                            <p>Waist: <span className="font-medium">{selectedPlayerDetails.waist_size}"</span></p>
                          )}
                          {selectedPlayerDetails.trouser_length && (
                            <p>Trouser Length: <span className="font-medium">{selectedPlayerDetails.trouser_length}"</span></p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Player Login Status */}
                    <div className="mt-3 p-3 rounded border" style={{
                      backgroundColor: selectedPlayerDetails.player_email ? '#f0fdf4' : '#fef3c7',
                      borderColor: selectedPlayerDetails.player_email ? '#bbf7d0' : '#fde68a'
                    }}>
                      {selectedPlayerDetails.player_email ? (
                        <div>
                          <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            Player Login Enabled
                          </p>
                          <p className="text-sm text-green-600 mt-1">
                            Email: {selectedPlayerDetails.player_email}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Player Login Not Enabled
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            Add an email address to enable their own login access.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 text-amber-700 border-amber-300 hover:bg-amber-50"
                            onClick={startEditProfile}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Add Email
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Edit Mode */}
                {isEditingProfile && (
                  <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-lg p-4 space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={editProfileData.full_name}
                        onChange={(e) => setEditProfileData({ ...editProfileData, full_name: e.target.value })}
                        placeholder="Child's full name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={editProfileData.date_of_birth}
                        onChange={(e) => setEditProfileData({ ...editProfileData, date_of_birth: e.target.value })}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Gender</Label>
                        <Select
                          value={editProfileData.gender}
                          onValueChange={(value) => setEditProfileData({ ...editProfileData, gender: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Skill Level</Label>
                        <Select
                          value={editProfileData.skill_level}
                          onValueChange={(value) => setEditProfileData({ ...editProfileData, skill_level: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Medical Notes</Label>
                      <Textarea
                        value={editProfileData.medical_notes}
                        onChange={(e) => setEditProfileData({ ...editProfileData, medical_notes: e.target.value })}
                        placeholder="Any medical conditions, allergies, or special needs..."
                        rows={3}
                      />
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <Mail className="h-4 w-4 text-purple-500" />
                        Player Login Access
                      </h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Player Email</Label>
                          <Input
                            type="email"
                            placeholder="child@email.com"
                            value={editProfileData.player_email}
                            onChange={(e) => {
                              const newEmail = e.target.value;
                              setEditProfileData({ ...editProfileData, player_email: newEmail });
                              // Real-time validation
                              if (newEmail && user?.email && newEmail.toLowerCase().trim() === user.email.toLowerCase().trim()) {
                                setEmailError('Player email cannot be the same as your (parent) email. The player email will be used for your child\'s own login account.');
                              } else {
                                setEmailError('');
                              }
                            }}
                            className={`text-sm ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Player Mobile</Label>
                          <Input
                            type="tel"
                            placeholder="555-123-4567"
                            value={editProfileData.player_mobile}
                            onChange={(e) => setEditProfileData({ ...editProfileData, player_mobile: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      {emailError && (
                        <Alert className="mt-3 bg-amber-50 border-amber-200">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <AlertDescription className="text-amber-800 text-sm">
                            {emailError}
                          </AlertDescription>
                        </Alert>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        This email will be used for your child's own login account to access their player dashboard.
                      </p>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <Shirt className="h-4 w-4 text-blue-500" />
                        Uniform Information
                      </h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Shirt Name</Label>
                          <Input
                            placeholder="Name on shirt"
                            value={editProfileData.shirt_name}
                            onChange={(e) => setEditProfileData({ ...editProfileData, shirt_name: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Shirt Number</Label>
                          <Input
                            type="number"
                            placeholder="Jersey number"
                            value={editProfileData.shirt_number}
                            onChange={(e) => setEditProfileData({ ...editProfileData, shirt_number: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <Ruler className="h-4 w-4 text-green-500" />
                        Measurements
                      </h5>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Chest Size (in)</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 32"
                            value={editProfileData.chest_size}
                            onChange={(e) => setEditProfileData({ ...editProfileData, chest_size: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Shirt Length (in)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="e.g., 26"
                            value={editProfileData.shirt_length}
                            onChange={(e) => setEditProfileData({ ...editProfileData, shirt_length: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Sleeves</Label>
                          <Select
                            value={editProfileData.sleeves || ''}
                            onValueChange={(value) => setEditProfileData({ ...editProfileData, sleeves: value })}
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="short">Short</SelectItem>
                              <SelectItem value="half">Half</SelectItem>
                              <SelectItem value="full">Full</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Waist Size (in)</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 28"
                            value={editProfileData.waist_size}
                            onChange={(e) => setEditProfileData({ ...editProfileData, waist_size: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Trouser Length (in)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="e.g., 36"
                            value={editProfileData.trouser_length}
                            onChange={(e) => setEditProfileData({ ...editProfileData, trouser_length: e.target.value })}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={cancelEditProfile}>
                        Cancel
                      </Button>
                      <Button className="bg-blue-500 hover:bg-blue-600" onClick={saveProfileChanges}>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentChildren;
